import { useEffect, useMemo, useState } from 'react';
import styles from './AnalysisDrawer.module.css';
import { LineChart, type Series } from '@/components/charts/LineChart';
import { Popover } from '@/components/ui/Popover';
import { Menu } from '@/components/ui/Menu';
import { Calendar } from '@/components/ui/Calendar';
import { useMetricSeries } from '@/hooks/useMetricSeries';
import { METRICS, METRIC_BY_KEY, formatMetric, type MetricKey } from '@/lib/metrics';
import type { DateRange, WindowToken } from '@/lib/dateRange';
import { CompetitionPanel } from './CompetitionPanel';

export interface AnalysisTarget {
  category?: string | undefined;
  city?: string | undefined;
  sku?: string | undefined;
}

const RANGES: Array<{ token: WindowToken; label: string }> = [
  { token: 'last_30_days', label: '1M' },
  { token: 'last_90_days', label: '3M' },
];

/**
 * 6M and 1Y are offered through Custom rather than as presets. city_daily is a
 * 503M-row table and the API has no window token beyond 90 days; a one-click
 * year would scan far more than a drawer should, so the longer ranges go
 * through the calendar where the cost is a deliberate choice.
 */

const STEPS = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
];

/**
 * "My Analysis" drawer — Figma 3727:30363 (chart) and 4139:78331 (table).
 *
 * Metric selection, chart/table switch and the range control all live here;
 * one grouped read of `products` backs every ticked metric, because they are
 * all columns on the same daily row.
 */
export function AnalysisDrawer({
  customerName,
  target,
  labels: scopeLabels,
  range: pageRange,
  onClose,
}: {
  customerName: string;
  target: AnalysisTarget;
  labels: string[];
  range: DateRange;
  onClose: () => void;
}) {
  const [tab, setTab] = useState<'mine' | 'competition'>('mine');
  const [lens, setLens] = useState<'OSA' | 'DOI'>('OSA');
  const [viz, setViz] = useState<'chart' | 'table'>('chart');
  const [range, setRange] = useState<DateRange>(pageRange);
  const [step, setStep] = useState('daily');
  const [selected, setSelected] = useState<MetricKey[]>(['sales', 'wt_osa', 'listing']);

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('keydown', onKey); };
  }, [onClose]);

  const plotKeys = useMemo(
    () => (lens === 'OSA' ? selected : (['inventory', 'units'] as MetricKey[])),
    [lens, selected],
  );
  const q = useMetricSeries(customerName, target, plotKeys, range, tab === 'mine');

  const toggle = (k: MetricKey): void => {
    setSelected((prev) => (prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k]));
  };

  // Percent metrics share a 0–100 axis; mixing a currency series onto it would
  // flatten both, so a currency selection is normalised to its own max.
  const series: Series[] = useMemo(() => {
    if (!q.data) return [];
    return plotKeys
      .map((k) => METRIC_BY_KEY.get(k))
      .filter((m): m is NonNullable<typeof m> => Boolean(m?.available))
      .map((m) => {
        const raw = q.data.values[m.key] ?? [];
        const max = Math.max(1, ...raw.filter((v): v is number => v != null));
        const scaled = m.unit === 'percent' ? raw : raw.map((v) => (v == null ? null : (v / max) * 100));
        return {
          key: m.key,
          label: m.label,
          color: m.color,
          points: scaled,
          format: (v: number) =>
            m.unit === 'percent' ? formatMetric(m, v) : formatMetric(m, (v / 100) * max),
        };
      });
  }, [q.data, plotKeys]);

  const latest = (k: MetricKey): string => {
    const m = METRIC_BY_KEY.get(k);
    const vals = q.data?.values[k]?.filter((v): v is number => v != null) ?? [];
    const last = vals.at(-1);
    return m && last != null ? formatMetric(m, last) : '—';
  };

  return (
    <div className={styles.backdrop} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className={styles.drawer} role="dialog" aria-modal="true" aria-label="Analysis">
        <div className={styles.head}>
          <div className={styles.headLeft}>
            <span className={styles.headLabel}>Showing results for:</span>
            {scopeLabels.length > 0
              ? scopeLabels.map((l) => <span className={styles.tag} key={l}>{l}</span>)
              : <span className={styles.tag}>All</span>}
          </div>
          <button type="button" className={styles.close} aria-label="Close" onClick={onClose}>×</button>
        </div>

        <div className={styles.tabs} role="tablist">
          <button type="button" role="tab" aria-selected={tab === 'mine'}
            className={`${styles.tab} ${tab === 'mine' ? styles.active : ''}`}
            onClick={() => { setTab('mine'); }}>My Analysis</button>
          <button type="button" role="tab" aria-selected={tab === 'competition'}
            className={`${styles.tab} ${tab === 'competition' ? styles.active : ''}`}
            onClick={() => { setTab('competition'); }}>Competition Analysis</button>
        </div>

        {tab === 'mine' ? (
          <>
            <div className={styles.controls}>
              <div className={styles.pillGroup}>
                {(['OSA', 'DOI'] as const).map((l) => (
                  <button key={l} type="button"
                    className={`${styles.pill} ${lens === l ? styles.active : ''}`}
                    onClick={() => { setLens(l); }}>{l}</button>
                ))}
              </div>
              <div className={styles.vizToggle}>
                Data Visualization
                <span className={styles.vizBtns}>
                  <button type="button" aria-label="Table view"
                    className={`${styles.vizBtn} ${viz === 'table' ? styles.active : ''}`}
                    onClick={() => { setViz('table'); }}>▤</button>
                  <button type="button" aria-label="Chart view"
                    className={`${styles.vizBtn} ${viz === 'chart' ? styles.active : ''}`}
                    onClick={() => { setViz('chart'); }}>▥</button>
                </span>
              </div>
            </div>

            {lens === 'OSA' && (
              <div className={styles.metrics}>
                {METRICS.map((m) => {
                  const on = selected.includes(m.key);
                  return (
                    <button
                      key={m.key}
                      type="button"
                      disabled={!m.available}
                      title={m.reason ?? m.label}
                      className={`${styles.metric} ${on ? styles.on : ''}`}
                      style={on ? { borderColor: m.color } : undefined}
                      onClick={() => { toggle(m.key); }}
                    >
                      <span className={styles.metricTop}>
                        <span className={styles.box} style={on ? { background: m.color, borderColor: m.color } : undefined}>
                          {on ? '✓' : ''}
                        </span>
                        {m.label}
                      </span>
                      <span className={styles.metricVal}>{m.available ? latest(m.key) : '—'}</span>
                      {!m.available && <span className={styles.metricSub}>not at this grain</span>}
                    </button>
                  );
                })}
              </div>
            )}

            <div className={styles.panel}>
              <div className={styles.card}>
                <div className={styles.cardHead}>
                  <span className={styles.ranges}>
                    <Popover
                      trigger={({ toggle: t }) => (
                        <button
                          type="button"
                          className={`${styles.range} ${range.token ? '' : styles.active}`}
                          onClick={t}
                        >
                          🗓 Custom
                        </button>
                      )}
                    >
                      {({ close }) => (
                        <Calendar
                          value={range}
                          onApply={(next) => { setRange(next); close(); }}
                          onCancel={close}
                        />
                      )}
                    </Popover>
                    {RANGES.map((r) => (
                      <button key={r.token} type="button"
                        className={`${styles.range} ${range.token === r.token ? styles.active : ''}`}
                        onClick={() => { setRange({ token: r.token }); }}>{r.label}</button>
                    ))}
                  </span>
                  <span className={styles.step}>
                    Time Step:
                    <Popover align="right"
                      trigger={({ toggle: t }) => (
                        <button type="button" className={styles.range} onClick={t}>
                          {STEPS.find((s) => s.value === step)?.label} ⌄
                        </button>
                      )}>
                      {({ close }) => (
                        <Menu options={STEPS} value={step}
                          onSelect={(v) => { setStep(v); close(); }} />
                      )}
                    </Popover>
                  </span>
                </div>

                {q.isLoading && <p className={styles.state}>Loading series…</p>}
                {q.isError && <p className={styles.state}>{q.error.message}</p>}
                {!q.isLoading && !q.isError && q.data && (
                  viz === 'chart' ? (
                    <LineChart labels={q.data.labels} series={series} yMax={100} height={360} />
                  ) : (
                    <SeriesTable labels={q.data.labels} series={series} />
                  )
                )}
                {!q.isLoading && !q.isError && !q.data && (
                  <p className={styles.state}>Select at least one metric.</p>
                )}
                <p className={styles.note}>
                  Non-percent metrics are scaled to their own maximum so they share the 0–100 axis;
                  the tooltip shows real values.
                </p>
              </div>
            </div>
          </>
        ) : (
          <div className={styles.panel}>
            <CompetitionPanel
              customerName={customerName}
              category={target.category}
              city={target.city}
              range={range}
            />
          </div>
        )}
      </div>
    </div>
  );
}

function SeriesTable({ labels, series }: { labels: string[]; series: Series[] }) {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr>
            <th style={{ textAlign: 'left', padding: '10px 12px', background: 'var(--bg-table-header)' }}>Date</th>
            {series.map((s) => (
              <th key={s.key} style={{ textAlign: 'right', padding: '10px 12px', background: 'var(--bg-table-header)' }}>
                {s.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {labels.map((l, i) => (
            <tr key={l}>
              <td style={{ padding: '10px 12px', borderBottom: '1px solid var(--border-cell)' }}>{l}</td>
              {series.map((s) => {
                const v = s.points[i];
                return (
                  <td key={s.key} style={{ padding: '10px 12px', textAlign: 'right', borderBottom: '1px solid var(--border-cell)' }}>
                    {v == null ? '—' : s.format ? s.format(v) : v.toFixed(1)}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
